import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { MedicationInfo } from 'components/Common/Medication/types'
import { MedicationsFormField } from './formField'
import { createAnswerItemsFromValues, getAnswerValuesFromItems } from '../../../helpers'
import { formatAssessmentMedications } from '../../../hooks/helpers'
import { AdapterOptions, SectionAdapter } from '../../../adapters/types'
import { FlatAnswerMutation } from '../../../adapters/types'
import { RocFormData } from '../../types/RocFormData'
import { RocAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { AllQuestionnaireItemType } from '../../../hooks/constants'

/**
 * Medications subsection adapter
 */
export class MedicationsAdapter extends SectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const medications = formatAssessmentMedications(flatAnswers)

    const medicationSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_SUMMARY
    )?.at(0)

    return {
      [MedicationsFormField.MEDICATIONS]: medications,
      [MedicationsFormField.MEDICATION_SUMMARY]: medicationSummary ?? ''
    }
  }

  buildFlatAnswers(formData: RocFormData): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_SUMMARY,
        value: formData[MedicationsFormField.MEDICATION_SUMMARY]
      }
    ]

    // Handle medications list using existing pattern
    const formMedications: MedicationInfo[] | undefined = formData[MedicationsFormField.MEDICATIONS]
    if (formMedications && Array.isArray(formMedications)) {
      // Reverse the order to match most recent first
      const medicationAnswers = [...formMedications].reverse().map((medication) => {
        const questionnaireItemsAndValues: [AllQuestionnaireItemType, unknown][] = [
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DRUGBANK_PCID,
            medication.productConceptId
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_NAME,
            medication.name
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_ROUTE,
            medication.route
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_NUMBER,
            medication.strength?.number
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_UNIT,
            medication.strength?.unit
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INDICATIONS,
            medication.indications
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_FREQUENCY,
            medication.frequency
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_NUMBER,
            medication.dosage?.number
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_UNIT,
            medication.dosage?.unit
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INSTRUCTIONS,
            medication.instructions
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_USAGE_STATUS,
            medication.usageStatus
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_START_DATE,
            medication.startDate
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DISCONTINUE_DATE,
            medication.discontinueDate
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_CONTROLLED,
            medication.controlled
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_LIQUID,
            medication.liquid
          ],
          [
            RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INFECTION,
            medication.infection
          ]
        ]

        return {
          item: createAnswerItemsFromValues(questionnaireItemsAndValues)
        }
      })

      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO,
        value: medicationAnswers
      })
    }

    return mutations
  }
}
